import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Cache auth result for 90 seconds: avoids re-hitting /auth/me on every navigation
const AUTH_CACHE_TTL_MS = 90_000;
let authCache = null; // { user, expiresAt }

export const invalidateAuthCache = () => { authCache = null; };

const ONBOARDING_EXEMPT_ROUTES = [
  '/onboarding', '/profile', '/subscription', '/contact', '/admin'
];

const SUBSCRIPTION_EXEMPT_ROUTES = [
  '/pricing', '/payment-success', '/onboarding', '/subscription',
  '/contact', '/admin', '/profile'
];

const ProtectedRoute = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [authState, setAuthState] = useState({ status: 'loading', user: null });

  useEffect(() => {
    const checkAuth = async () => {
      const sessionToken = localStorage.getItem('session_token');
      if (!sessionToken) {
        setAuthState({ status: 'unauthenticated', user: null });
        return;
      }

      // Use cache if valid
      if (authCache && Date.now() < authCache.expiresAt) {
        const user = authCache.user;
        applyRedirects(user, location, navigate);
        setAuthState({ status: 'authenticated', user });
        return;
      }

      try {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { 'Authorization': `Bearer ${sessionToken}` }
        });
        const user = response.data;

        // Cache the result
        authCache = { user, expiresAt: Date.now() + AUTH_CACHE_TTL_MS };

        applyRedirects(user, location, navigate);
        setAuthState({ status: 'authenticated', user });
      } catch (error) {
        localStorage.removeItem('session_token');
        authCache = null;
        setAuthState({ status: 'unauthenticated', user: null });
      }
    };

    setAuthState({ status: 'loading', user: null });
    checkAuth();
  }, [location.pathname]); // only re-check when path changes

  if (authState.status === 'loading') {
    return (
      <div className="flex items-center justify-center min-h-screen" style={{ backgroundColor: 'var(--navy-50)' }}>
        <div className="text-center">
          <div
            className="inline-block h-10 w-10 animate-spin rounded-full border-4 border-solid border-r-transparent mb-4"
            style={{ borderColor: 'var(--navy-900)', borderRightColor: 'transparent' }}
          />
          <p className="text-sm text-gray-500">Loading your workspace...</p>
        </div>
      </div>
    );
  }

  if (authState.status === 'unauthenticated') {
    window.location.href = '/login';
    return null;
  }

  return children;
};

function applyRedirects(user, location, navigate) {
  const path = location.pathname;

  if (user.is_admin && path !== '/admin') {
    navigate('/admin', { replace: true });
    return;
  }

  const isOnboardingExempt = ONBOARDING_EXEMPT_ROUTES.some(r => path.startsWith(r));
  if (!user.onboarding_completed && !isOnboardingExempt) {
    navigate('/onboarding', { replace: true });
    return;
  }

  const isSubscriptionExempt = SUBSCRIPTION_EXEMPT_ROUTES.some(r => path.startsWith(r));
  const hasActiveSub = user.subscription_status === 'active'
    || user.subscription_status === 'trialing'
    || user.subscription_plan === 'lifetime'
    || user.subscription_plan === 'free'
    || !user.subscription_plan;

  if (!hasActiveSub && !isSubscriptionExempt && !user.is_admin) {
    navigate('/pricing', { replace: true });
  }
}

export default ProtectedRoute;
